import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Plus, X, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DiagnosisSelect({ selectedIds = [], onChange }) {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]); // full objects
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // hydrate selected objects when selectedIds prop changes
  useEffect(() => {
    async function hydrate() {
      if (!selectedIds || selectedIds.length === 0) {
        setSelected([]);
        return;
      }
      try {
        const { data } = await api.get("/diagnoses");
        const map = new Map(data.map((d) => [d.id, d]));
        setSelected(selectedIds.map((id) => map.get(id)).filter(Boolean));
      } catch (e) {
        // ignore
      }
    }
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(",")]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/diagnoses", { params: query ? { q: query } : {} });
        setSuggestions(data);
      } catch (e) {
        setSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function pick(dx) {
    if (selected.find((s) => s.id === dx.id)) {
      setOpen(false);
      setQuery("");
      return;
    }
    const next = [...selected, dx];
    setSelected(next);
    onChange && onChange(next.map((n) => n.id));
    setQuery("");
    setOpen(false);
  }

  function removeItem(id) {
    const next = selected.filter((s) => s.id !== id);
    setSelected(next);
    onChange && onChange(next.map((n) => n.id));
  }

  async function createNew() {
    const name = query.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/diagnoses", { name });
      pick(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const filtered = suggestions.filter((s) => !selected.find((x) => x.id === s.id));
  const exactMatch = filtered.find((s) => s.name_lower === query.trim().toLowerCase());

  return (
    <div ref={boxRef} className="relative" data-testid="diagnosis-select">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map((d) => (
          <span key={d.id} data-testid={`dx-chip-${d.id}`} className="pf-chip-primary !py-1 pr-1 items-center inline-flex gap-1">
            {d.name}
            <button type="button" onClick={() => removeItem(d.id)} className="ml-1 p-0.5 rounded hover:bg-white/20">
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
        <input
          data-testid="diagnosis-search-input"
          className="pf-input pl-9"
          placeholder={t("searchOrCreateDiagnosis")}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered[0] && filtered[0].name_lower === query.trim().toLowerCase()) {
                pick(filtered[0]);
              } else if (query.trim()) {
                createNew();
              }
            }
          }}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-pf-border rounded-lg shadow-card max-h-64 overflow-auto">
          {filtered.slice(0, 10).map((d) => (
            <button
              key={d.id}
              type="button"
              data-testid={`dx-suggestion-${d.id}`}
              onClick={() => pick(d)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-pf-bg flex items-center justify-between"
            >
              <span>{d.name}</span>
              {d.usage_count > 0 && (
                <span className="text-xs text-pf-text-secondary">{d.usage_count}×</span>
              )}
            </button>
          ))}
          {query.trim() && !exactMatch && (
            <button
              type="button"
              data-testid="dx-create-btn"
              onClick={createNew}
              className="w-full text-left px-3 py-2 text-sm hover:bg-pf-primary-light border-t border-pf-border text-pf-primary flex items-center gap-2 font-medium"
            >
              <Plus size={12} weight="bold" /> {t("createDiagnosis")}: “{query.trim()}”
            </button>
          )}
          {filtered.length === 0 && !query.trim() && (
            <div className="px-3 py-3 text-xs text-pf-text-secondary">—</div>
          )}
        </div>
      )}
    </div>
  );
}
